'use client'

import { clsx } from 'clsx'
import Image, { getImageProps, type ImageProps } from 'next/image'
import {
  type ComponentProps,
  type FC,
  type ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { useCdnMediaServer } from '../../../cdn'
import {
  getImageUrlWithWebpExtension,
  IMAGE_PRODUCT_FALLBACK_SRC,
  IMAGE_PRODUCT_PRESCRIPTION_FALLBACK_SRC,
  useCdnHostedImageLoader,
} from '../../../image'

type ProductPictureProps = ComponentProps<'picture'> & {
  /**
   * Custom fallback image
   */
  fallbackSrc?: string

  /**
   * Props for the Next.js `Image` component. If the product image is not provided,
   * do a fallback depending on prescription `flag` or `fallbackSrc` prop
   */
  imageProps: Omit<ImageProps, 'loader' | 'src'> & { src?: string }

  /**
   * Flag a prescription product
   */
  prescription?: boolean
}

/**
 * The ProductPicture component displays a product image using a CDN source.
 * It has a fallback mechanism for broken or failed images. Provide different
 * fallbacks for prescription and non-prescription products.
 *
 * Solution is inspired by https://stackoverflow.com/a/70544058.
 *
 */
/* eslint-disable-next-line max-statements -- the rule was disabled before a comment became required */
export const ProductPicture: FC<ProductPictureProps> = (props) => {
  const { className, fallbackSrc, imageProps, prescription, ...restProps } =
    props
  const { setMediaServerOrigin } = useCdnMediaServer()

  const {
    alt,
    className: imageClassNameProp,
    onError: onErrorProp,
    onLoad: onLoadProp,
    src,
    ...restImageProps
  } = imageProps

  const loader = useCdnHostedImageLoader()

  const [error, setError] = useState(false)

  // Reset fallback state if `src` changes
  useEffect(() => {
    if (src) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- don't reference, refactor if touched
      setError(false)
    }
  }, [src])

  const onError: Required<ImageProps>['onError'] = useCallback(
    (event) => {
      setError(true)
      onErrorProp?.(event)
    },
    [onErrorProp],
  )

  const onLoad: Required<ImageProps>['onLoad'] = useCallback(
    (event) => {
      if (
        event.target instanceof HTMLImageElement &&
        // Broken image
        event.target.naturalWidth === 0
      ) {
        setError(true)
      }
      onLoadProp?.(event)
    },
    [onLoadProp],
  )

  let image: ReactElement | null = null

  const imageClassName = clsx('product-picture__image', imageClassNameProp)

  if (error) {
    image = (
      <Image
        alt={alt}
        className={imageClassName}
        {...restImageProps}
        src={setMediaServerOrigin(IMAGE_PRODUCT_FALLBACK_SRC)}
        unoptimized
      />
    )
  } else if (src) {
    const { props: webpImageProps } = getImageProps({
      ...restImageProps,
      alt,
      loader,
      src: getImageUrlWithWebpExtension(src),
    })

    image = (
      <>
        <source srcSet={webpImageProps.srcSet} type="image/webp" />
        <Image
          alt={alt}
          className={imageClassName}
          {...restImageProps}
          loader={loader}
          src={src}
        />
      </>
    )
  } else {
    const fallback =
      fallbackSrc ??
      setMediaServerOrigin(
        prescription
          ? IMAGE_PRODUCT_PRESCRIPTION_FALLBACK_SRC
          : IMAGE_PRODUCT_FALLBACK_SRC,
      )

    image = (
      <Image
        alt={alt}
        className={clsx(imageClassName, 'product-picture__image_fallback')}
        {...restImageProps}
        src={fallback}
        unoptimized
      />
    )
  }

  return (
    <picture
      className={clsx('product-picture', className)}
      {...restProps}
      onError={onError}
      onLoad={onLoad}
    >
      {image}
    </picture>
  )
}
